import React, { useState } from "react";
import Banner from "../components/allbanner";
import { supabase } from "../shared/supabaseclient";

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    appointmentType: "",
  });

  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showToast = (message, type = "success", duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), duration);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, message, appointmentType } = formData;

    // ✅ Validation
    if (!name || !email || !phone || !message || !appointmentType) {
      showToast("⚠️ Please fill out all fields before submitting.", "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("⚠️ Please enter a valid email address.", "error");
      return;
    }

    const phoneRegex = /^\+?\d{10,15}$/;
    if (!phoneRegex.test(phone)) {
      showToast("⚠️ Please enter a valid phone number (digits only).", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      // ✅ 1. Save to Supabase table
      const { error } = await supabase.from("contact_requests").insert([
        {
          name,
          email,
          phone,
          message,
          appointment_type: appointmentType,
        },
      ]);

      if (error) {
        console.error("Supabase error:", error);
        showToast("⚠️ Something went wrong. Please try again later.", "error");
        return;
      }

      // ✅ 2. Send email via Supabase Edge Function
      await fetch("https://tagmecosqdepdudzytpe.supabase.co/functions/v1/dynamic-endpoint", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, message, appointmentType }),
      }).catch((err) => console.error("Email send error:", err));

      // ✅ 3. Show success message
      showToast("✅ Thank you for contacting Isuzu Gujranwala Motors! We'll reach out shortly.", "success");

      // ✅ 4. Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        appointmentType: "",
      });
    } catch (err) {
      console.error("Submit error:", err);
      showToast("⚠️ Something went wrong. Please try again later.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 via-white to-gray-100 overflow-hidden relative">
      <Banner
        title="Get In Touch With Us"
        description="Whether you’re booking a service, exploring sales, or need genuine parts — we’re here for you."
      />

      {/* Main Section */}
      <section className="py-20 px-6 container mx-auto animate-fadeInUp">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* LEFT - Contact Info */}
          <div className="animate-fadeInUp delay-100">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-8 border-l-4 border-[#D71920] pl-3">
              Contact Information
            </h2>

            <div className="space-y-8 mb-12">
              {[
                {
                  icon: "📍",
                  title: "Postal Address",
                  text: (
                    <>
                      ISUZU GUJRANWALA MOTORS <br />
                      Chand Da Qila, Gujranwala, 52250
                    </>
                  ),
                },
                {
                  icon: "📞",
                  title: "Phone & Support",
                  text: (
                    <>
                      Inquiry: 0312-9388531 <br />
                      
                    </>
                  ),
                },
                {
                  icon: "✉️",
                  title: "Email",
                  text: (
                    <>
                      info@isuzugujranwala.com <br />
                      atique@isuzugujranwala.com<br/>
                    </>
                  ),
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200"
                  style={{ animationDelay: `${0.2 + i * 0.2}s` }}
                >
                  <div className="text-[#D71920] text-3xl mr-4 animate-bounceSlow">{item.icon}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Google Map */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <iframe
                title="Isuzu Gujranwala Motors Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3380.4902663368184!2d74.20102707530748!3d32.08303291912864!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391f2bb5f81bdad9%3A0x942b82d4c443f196!2sGujranwala%20Truck%20Dealer!5e0!3m2!1sen!2s!4v1771934742174!5m2!1sen!2s"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              ></iframe>
              
            </div>
          </div>

          {/* RIGHT - Contact Form */}
          <div className="animate-fadeInUp delay-200">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-8 border-l-4 border-[#D71920] pl-3">
              Send Us a Message
            </h2>

            <form
              onSubmit={handleSubmit}
              className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 space-y-6 hover:shadow-2xl transition-all duration-500"
            >
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D71920] focus:border-[#D71920] transition-all duration-300"
                />
              </div>

              {/* Email + Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { id: "email", type: "email", label: "Email" },
                  { id: "phone", type: "tel", label: "Phone" },
                ].map((input) => (
                  <div key={input.id}>
                    <label htmlFor={input.id} className="block text-gray-700 mb-2">
                      {input.label}
                    </label>
                    <input
                      type={input.type}
                      id={input.id}
                      name={input.id}
                      value={formData[input.id]}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D71920] focus:border-[#D71920] transition-all duration-300"
                    />
                  </div>
                ))}
              </div>

              {/* Appointment Type */}
              <div>
                <label htmlFor="appointmentType" className="block text-gray-700 mb-2 font-medium">
                  Query Type
                </label>
                <div className="relative group">
                  <select
                    id="appointmentType"
                    name="appointmentType"
                    value={formData.appointmentType}
                    onChange={handleChange}
                    required
                    className="w-full appearance-none px-4 py-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#D71920] text-gray-700 pr-10 transition duration-200 shadow-sm hover:shadow-md"
                  >
                    <option value="">Select an option</option>
                    <option value="sales">💼 Sales Inquiry</option>
                    <option value="service">🧰 Service Appointment</option>
                    <option value="parts">🔧 Parts Order</option>
                    <option value="general">📩 General Inquiry</option>
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-red-700 group-hover:text-red-800 transition">
                    ▼
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D71920] transition-all duration-300"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-md font-semibold shadow-md transform transition-all duration-300 
    ${isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#D71920] hover:shadow-lg hover:bg-[#b81419] hover:-translate-y-[2px] text-white"
                  }`}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>

            </form>
          </div>
        </div>
      </section>

      {/* Toast Popup */}
      {toast.visible && (
        <div
          className={`fixed top-8 right-4 z-50 px-6 py-4 rounded-xl shadow-xl font-semibold text-white transition-transform transform ${toast.type === "success" ? "bg-green-600" : "bg-red-600"
            } animate-slideIn`}
        >
          {toast.message}
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp { opacity: 0; animation: fadeInUp 1s ease-out forwards; }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }

        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounceSlow { animation: bounceSlow 3s ease-in-out infinite; }

        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slideIn { animation: slideIn 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default ContactPage;
